// Shared Chart.js options for FCC analytics. PBI-style bar+line
// dashboards using Reach brand palette. Pill data labels via
// chartjs-plugin-datalabels (registered globally below).
//
// Brand palette source: /home/shayne/Work/Reach/New Brand/reach-brand/tokens/tokens.json

import { Chart } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import type { ChartOptions, Color, ScriptableContext } from 'chart.js'

Chart.register(ChartDataLabels)

const palette = (isDark: boolean) =>
  isDark
    ? {
        text: '#FAF7F5', // primary.off-white
        textMuted: '#C7C2B5', // gray.light
        grid: 'rgba(238, 236, 230, 0.12)',
      }
    : {
        text: '#1E1A15', // primary.black
        textMuted: '#524F4B', // gray.med (improved contrast on white)
        grid: 'rgba(30, 26, 21, 0.18)',
      }

// Brand-aligned series colors. PBI semantics: purple bars, peach line/bars,
// blue line for cool/ratio metrics. Pastels rendered solid (no alpha) so
// they read on white and dark surfaces.
export const SERIES_COLORS = {
  // Primary bar — accent.purple (#D4C9ED) with darker border
  barPrimary: '#D4C9ED',
  barPrimaryBorder: '#8A75C4',
  // Secondary bar / line — accent.peach (#FACBA1) with darker border
  warm: '#FACBA1',
  warmBorder: '#E89A4F',
  // Ratio line — accent.blue (#C2CFE5) darker border
  cool: '#C2CFE5',
  coolBorder: '#7B95C2',
  // Auth-success bars — accent.green (#DAF2BF) with darker border
  good: '#DAF2BF',
  goodBorder: '#8BAE5D',
  // Neutral — primary.olive (#999177)
  neutral: '#999177',
  neutralLight: '#C7C2B5',
}

export const formatUsd = (value: number): string =>
  `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`

export const formatUsdShort = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(2)}K`
  return formatUsd(value)
}

export const formatCount = (value: number): string =>
  value.toLocaleString('en-US', { maximumFractionDigits: 0 })

export const formatCountShort = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return formatCount(value)
}

export const formatPercent = (value: number, fractionDigits = 2): string =>
  `${(value * 100).toFixed(fractionDigits)}%`

// Compute stride that yields ~maxLabels evenly spaced visible labels.
// Always shows first + last, and every Nth in between. Prevents pill clutter
// on daily series (e.g., 31 days → ~6 labels by default).
const computeStride = (total: number, maxLabels: number): number => {
  if (total <= maxLabels) return 1
  return Math.ceil(total / maxLabels)
}

// Common pill data label style. Background defaults to the dataset's border
// color (so the pill matches the series color). Override per-chart formatter.
//
// `maxLabels` caps how many pills render. Default 6 — enough to read trends
// without overlap. First and last point always shown.
export const pillLabel = (
  formatter: (value: number) => string,
  opts: {
    anchor?: 'start' | 'end' | 'center'
    align?: 'top' | 'bottom' | 'center' | 'end' | 'start'
    maxLabels?: number
  } = {},
) => {
  const maxLabels = opts.maxLabels ?? 6
  return {
    display: (ctx: ScriptableContext<'bar' | 'line'>) => {
      const total = ctx.dataset.data?.length ?? 0
      if (total === 0) return false
      const stride = computeStride(total, maxLabels)
      const i = ctx.dataIndex
      return i === 0 || i === total - 1 || i % stride === 0
    },
    anchor: opts.anchor ?? 'end',
    align: opts.align ?? 'top',
    backgroundColor: (ctx: ScriptableContext<'bar' | 'line'>) =>
      (ctx.dataset.borderColor as Color | undefined) ?? '#1E1A15',
    borderRadius: 8,
    padding: { top: 3, bottom: 3, left: 6, right: 6 },
    color: '#FFFFFF',
    font: { weight: 'bold' as const, size: 10 },
    formatter: (value: number) => formatter(value ?? 0),
    clamp: true,
    clip: false,
  }
}

// Shared bar dataset visual defaults. Charts spread these into each bar dataset.
export const BAR_BASE = {
  borderWidth: 0,
  borderRadius: 4,
  borderSkipped: false as const,
  categoryPercentage: 0.9,
  barPercentage: 0.85,
}

// Shared line dataset visual defaults.
export const LINE_BASE = {
  pointRadius: 4,
  borderWidth: 2,
  tension: 0.3,
}

// Build `scales` block for charts with left (`y`) and optional right (`y1`) axis.
// `leftStacked` toggles the stacked-bar variant used by BinMismatch.
export const dualAxisScales = (
  base: ChartOptions<'bar' | 'line'>,
  leftFormat: (value: number) => string,
  right?: { format: (value: number) => string; max?: number },
  leftStacked = false,
) => {
  const baseScales = (base.scales ?? {}) as Record<string, Record<string, unknown>>
  const yBase = (baseScales.y ?? {}) as Record<string, unknown>
  const yBaseTicks = (yBase.ticks ?? {}) as Record<string, unknown>
  const scales: Record<string, unknown> = {
    ...baseScales,
    y: {
      ...yBase,
      position: 'left' as const,
      ...(leftStacked ? { stacked: true } : {}),
      ticks: {
        ...yBaseTicks,
        callback: (value: string | number) => leftFormat(Number(value)),
      },
    },
  }
  if (right) {
    scales.y1 = {
      ...yBase,
      position: 'right' as const,
      ...(right.max !== undefined ? { max: right.max } : {}),
      grid: { drawOnChartArea: false },
      ticks: {
        ...yBaseTicks,
        callback: (value: string | number) => right.format(Number(value)),
      },
    }
  }
  return scales
}

// Tooltip callback that dispatches by dataset label. Each matcher returns
// formatted output or null to fall through. Last matcher acts as default.
export type TooltipMatcher = (
  label: string,
  value: number,
) => string | null

export const labeledTooltip = (...matchers: TooltipMatcher[]) => ({
  label(ctx: { dataset: { label?: string }; parsed: { y: number } }) {
    const label = ctx.dataset.label ?? ''
    const value = ctx.parsed.y
    for (const match of matchers) {
      const result = match(label, value)
      if (result !== null) return result
    }
    return `${label}: ${value}`
  },
})

// Defaults used by every analytics chart. Each chart layers its own
// scales/datalabels formatters on top.
export const baseChartOptions = (
  isDark: boolean,
): ChartOptions<'bar' | 'line'> => {
  const colors = palette(isDark)
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 20, right: 8, left: 4, bottom: 4 } },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          color: colors.text,
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 12,
          font: { size: 12 },
        },
      },
      tooltip: {
        // Always dark bg + light text — tooltip floats, doesn't follow page theme.
        backgroundColor: '#1E1A15',
        titleColor: '#FAF7F5',
        bodyColor: '#C7C2B5',
        borderColor: 'rgba(238,236,230,0.15)',
        borderWidth: 1,
        padding: 10,
      },
      // Disabled by default. Charts opt-in per dataset via `datalabels: pillLabel(...)`.
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: { color: colors.textMuted, maxTicksLimit: 12 },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        ticks: { color: colors.textMuted },
        grid: { color: colors.grid },
        border: { display: false },
        beginAtZero: true,
      },
    },
  }
}
